import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ChevronLeft, Crosshair, Loader2, MapPin, ShieldCheck, Sparkles, X } from 'lucide-react';
import GuidedLocationPicker from './GuidedLocationPicker';
import ZoneGpsPicker from './map/ZoneGpsPicker';
import { buildZoneIndex, resolveZoneCoords } from './map/zoneCoords';
import { CommanServices } from '@/services/commanServices';
import { realIncidentCorrectionLockReason } from '../utils/incidentCorrectionPolicy';

const toPoint = (value) => {
  const lat = value?.latitude ?? value?.lat;
  const lng = value?.longitude ?? value?.lng;
  return lat != null && lng != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng))
    ? { lat: Number(lat), lng: Number(lng) }
    : null;
};

const locationName = (loc) => loc?.auto_label || loc?.label || loc?.zone_name || loc?.atomic_value || 'Selected location';

const normalizeCoordinateSource = (value) => String(value || '').trim().toLowerCase();

const coordinateSourceForMode = (mode, selectedLocation, resolvedExistingPoint) => {
  if (mode === 'reporter') return 'reporter_accepted';
  if (mode === 'custom') return 'admin_selected';
  if (mode === 'inherited') return 'inherited';
  if (mode === 'official') {
    return normalizeCoordinateSource(
      selectedLocation?.gps_coordinate_source || resolvedExistingPoint?.source || 'location'
    );
  }
  return '';
};

const samePoint = (left, right) => {
  const leftPoint = toPoint(left);
  const rightPoint = toPoint(right);
  if (!leftPoint || !rightPoint) return leftPoint === rightPoint;
  return Math.abs(leftPoint.lat - rightPoint.lat) < 0.0000001
    && Math.abs(leftPoint.lng - rightPoint.lng) < 0.0000001;
};

export const mappingChangesIncidentFacts = ({
  projection = {},
  coordinateMode,
  selectedLocation,
  resolvedExistingPoint,
  selectedPreviewPoint,
}) => {
  if (!coordinateMode) return false;
  const currentSource = normalizeCoordinateSource(projection.coordinate_source);
  const proposedSource = coordinateSourceForMode(coordinateMode, selectedLocation, resolvedExistingPoint);
  const currentPoint = projection.official_coordinates || projection.coordinates;
  return currentSource !== proposedSource || !samePoint(currentPoint, selectedPreviewPoint);
};

const formatPoint = (value) => {
  const point = toPoint(value);
  return point ? `${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}` : 'No official GPS';
};

const formatSource = (value) => ({
  reporter_unverified: 'Reporter GPS (unverified)',
  reporter_accepted: 'Reporter GPS (accepted)',
  admin_selected: 'Administrator-selected GPS',
  inherited: 'Inherited parent GPS',
  location: 'Verified saved GPS',
  unavailable: 'GPS unavailable',
}[normalizeCoordinateSource(value)] || String(value || 'GPS unavailable').replaceAll('_', ' '));

export default function IncidentLocationMappingModal({
  isOpen,
  incident,
  locations = [],
  onClose,
  onMapped,
}) {
  const projection = incident?.map_projection || {};
  const knownLocationId = projection.location_id
    || (Array.isArray(incident?.location) ? incident.location.find((id) => id && id !== 'unknown') : null);
  const startsWithKnownLocation = !!knownLocationId;
  const reporterPoint = toPoint(projection.reporter_coordinates || incident?.gps_coordinates);
  const zoneIndex = useMemo(() => buildZoneIndex(locations), [locations]);
  const [step, setStep] = useState(startsWithKnownLocation ? 2 : 1);
  const [selection, setSelection] = useState(null);
  const [guidedSearch, setGuidedSearch] = useState({ active: true, selectedParentId: null, breadcrumb: [] });
  const [coordinateMode, setCoordinateMode] = useState('');
  const [customPoint, setCustomPoint] = useState(null);
  const [operationsArea, setOperationsArea] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const initialSelection = knownLocationId
      ? { location_id: knownLocationId, location_match: projection.location_name || incident?.location_names?.[0] }
      : null;
    setSelection(initialSelection);
    setStep(initialSelection ? 2 : 1);
    setGuidedSearch({ active: true, selectedParentId: null, breadcrumb: [] });
    setCoordinateMode('');
    setCustomPoint(null);
    setCorrectionReason('');
    setError('');
    CommanServices.getOperationsArea()
      .then((res) => { if (res?.status !== false) setOperationsArea(res?.data || null); })
      .catch(() => {});
  }, [incident?.id, isOpen, knownLocationId, projection.location_name]);

  const selectedLocation = selection?.location_id
    ? locations.find((loc) => String(loc.id) === String(selection.location_id))
    : null;
  const parentId = selection?.new_location?.parent_id || selectedLocation?.parent_id;
  const resolvedExistingPoint = selection?.location_id
    ? resolveZoneCoords(zoneIndex, selection.location_id)
    : null;
  const inheritedPoint = parentId ? resolveZoneCoords(zoneIndex, parentId) : null;
  const selectedOfficialPoint = resolvedExistingPoint
    ? { lat: resolvedExistingPoint.lat, lng: resolvedExistingPoint.lng }
    : null;
  const selectedIsInherited = !!resolvedExistingPoint?.inheritedFrom
    || String(selectedLocation?.gps_coordinate_source || '').toLowerCase() === 'inherited'
    || !!selectedLocation?.gps_is_inherited;
  const selectedHasLockedGps = !!selectedOfficialPoint && !selectedIsInherited;
  const availableInheritedPoint = selection?.new_location
    ? inheritedPoint
    : (selectedIsInherited ? resolvedExistingPoint : null);
  const displayName = selection?.location_match
    || locationName(selectedLocation)
    || (selection?.new_location ? `${selection.new_location.location_type}: ${selection.new_location.location_value}` : 'Selected location');
  const possibleLocations = Array.isArray(projection?.possible_locations)
    ? projection.possible_locations.filter((item) => item?.location_id && item?.location_name)
    : [];
  const selectedPreviewPoint = coordinateMode === 'custom'
    ? customPoint
    : coordinateMode === 'reporter'
      ? reporterPoint
      : coordinateMode === 'inherited'
        ? (availableInheritedPoint ? { lat: availableInheritedPoint.lat, lng: availableInheritedPoint.lng } : null)
        : selectedOfficialPoint;
  const primaryLocationChanges = Boolean(
    selection?.new_location
    || (selection?.location_id && String(selection.location_id) !== String(knownLocationId || ''))
  );
  const gpsMappingChanges = mappingChangesIncidentFacts({
    projection,
    coordinateMode,
    selectedLocation,
    resolvedExistingPoint,
    selectedPreviewPoint,
  });
  const protectedCorrection = primaryLocationChanges || gpsMappingChanges;
  const correctionLockReason = realIncidentCorrectionLockReason(incident);
  const correctionExpectedRevision = Number(incident?.definition_revision || 0);
  const proposedCoordinateSource = coordinateSourceForMode(
    coordinateMode,
    selectedLocation,
    resolvedExistingPoint
  );

  useEffect(() => {
    if (!isOpen || step !== 2 || coordinateMode) return;
    if (selectedHasLockedGps) setCoordinateMode('official');
    else if (availableInheritedPoint) setCoordinateMode('inherited');
    else if (reporterPoint) setCoordinateMode('reporter');
    else setCoordinateMode('custom');
  }, [availableInheritedPoint, coordinateMode, isOpen, reporterPoint, selectedHasLockedGps, step]);

  if (!isOpen) return null;

  const selectLocation = (nextSelection) => {
    if (!nextSelection || nextSelection.location_id === 'unknown') return;
    setSelection(nextSelection);
    setCoordinateMode('');
    setCustomPoint(null);
    setError('');
    setStep(2);
  };

  const confirm = async () => {
    if (!selection || !coordinateMode) return;
    if (protectedCorrection && correctionLockReason) {
      setError(correctionLockReason);
      return;
    }
    if (protectedCorrection && !correctionReason.trim()) {
      setError('Enter a reason for changing the incident location or official GPS.');
      return;
    }
    if (coordinateMode === 'custom' && !customPoint) {
      setError('Search for a place or drop a pin before confirming.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const response = await CommanServices.confirmIncidentMapLocation(incident.id, {
        location_id: selection.location_id || null,
        new_location: selection.new_location || null,
        coordinate_mode: coordinateMode,
        coordinates: coordinateMode === 'custom' ? customPoint : null,
        correction_reason: protectedCorrection ? correctionReason.trim() : null,
        correction_expected_revision: correctionExpectedRevision,
      });
      if (response?.status === false) throw new Error(response?.message || 'Could not confirm incident mapping.');
      await onMapped?.(response?.data);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Could not confirm incident mapping.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-950/55 p-4" data-testid="incident-location-mapping-modal">
      <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-blue-600">Step {step} of 2</p>
            <h2 className="mt-0.5 text-lg font-bold text-slate-900">
              {step === 1 ? 'Link or create the incident location' : 'Review and confirm official GPS'}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5">
          {step === 1 ? (
            <div className="space-y-4">
              {possibleLocations.length > 0 && (
                <section className="rounded-2xl border border-blue-200 bg-blue-50/70 p-4" data-testid="incident-location-gps-suggestions">
                  <div className="flex items-start gap-2">
                    <span className="rounded-lg bg-blue-600 p-2 text-white"><Sparkles className="h-4 w-4" /></span>
                    <div>
                      <h3 className="text-sm font-black text-slate-900">Possible saved locations</h3>
                      <p className="mt-0.5 text-[11px] leading-4 text-slate-600">
                        These Locations &amp; Zones are linked to GPS near the reporter point. Select one or continue searching below.
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {possibleLocations.map((item) => (
                      <button
                        key={item.location_id}
                        type="button"
                        onClick={() => selectLocation({
                          location_id: item.location_id,
                          location_match: item.location_name,
                        })}
                        className="flex min-w-0 items-center gap-2 rounded-xl border border-blue-200 bg-white px-3 py-2.5 text-left shadow-sm transition hover:border-blue-400 hover:bg-blue-50"
                      >
                        <MapPin className="h-4 w-4 shrink-0 text-blue-600" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-xs font-bold text-slate-800">{item.location_name}</span>
                          <span className="block text-[10px] font-semibold text-blue-700">
                            {item.match_type === 'same_gps' ? 'Same saved GPS' : `${item.distance_m} m from reporter GPS`}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                </section>
              )}
              <GuidedLocationPicker
                locations={locations}
                guidedSearch={guidedSearch}
                setGuidedSearch={setGuidedSearch}
                reporterGps={reporterPoint}
                originIncidentId={incident?.id}
                onApplyLocation={selectLocation}
                deferCreate
                className="!mt-0 !border-0 !bg-white !p-0 !shadow-none"
              />
            </div>
          ) : (
            <div className="space-y-5">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Primary location</p>
                <p className="mt-1 flex items-center gap-2 text-sm font-bold text-slate-800">
                  <MapPin className="h-4 w-4 text-blue-600" /> {displayName}
                </p>
              </div>

              {selectedHasLockedGps ? (
                <button type="button" onClick={() => setCoordinateMode('official')} className="w-full rounded-xl border-2 border-emerald-400 bg-emerald-50 p-4 text-left">
                  <span className="flex items-center gap-2 text-sm font-bold text-emerald-800"><ShieldCheck className="h-4 w-4" /> Use verified Locations &amp; Zones GPS</span>
                  <span className="mt-1 block font-mono text-xs text-emerald-700">{selectedOfficialPoint.lat.toFixed(6)}, {selectedOfficialPoint.lng.toFixed(6)}</span>
                  <span className="mt-2 block text-[11px] text-emerald-700">This verified location can only be moved from Locations &amp; Zones.</span>
                </button>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {availableInheritedPoint && (
                    <button type="button" onClick={() => setCoordinateMode('inherited')} className={`rounded-xl border-2 p-4 text-left transition ${coordinateMode === 'inherited' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-emerald-300'}`}>
                      <span className="text-sm font-bold text-slate-800">Use inherited parent GPS</span>
                      <span className="mt-1 block text-[11px] text-slate-500">Nearest mapped ancestor: {availableInheritedPoint.matchedName}</span>
                      <span className="mt-1 block font-mono text-[11px] text-slate-600">{availableInheritedPoint.lat.toFixed(6)}, {availableInheritedPoint.lng.toFixed(6)}</span>
                    </button>
                  )}
                  {reporterPoint && (
                    <button type="button" onClick={() => setCoordinateMode('reporter')} className={`rounded-xl border-2 p-4 text-left transition ${coordinateMode === 'reporter' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`}>
                      <span className="text-sm font-bold text-slate-800">Use reporter GPS</span>
                      <span className="mt-1 block text-[11px] text-slate-500">Accept the captured device point as the official location.</span>
                      <span className="mt-1 block font-mono text-[11px] text-slate-600">{reporterPoint.lat.toFixed(6)}, {reporterPoint.lng.toFixed(6)}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      const startingPoint = customPoint
                        || reporterPoint
                        || (availableInheritedPoint ? { lat: availableInheritedPoint.lat, lng: availableInheritedPoint.lng } : null)
                        || selectedOfficialPoint;
                      if (startingPoint) setCustomPoint(startingPoint);
                      setCoordinateMode('custom');
                    }}
                    className={`rounded-xl border-2 p-4 text-left transition ${coordinateMode === 'custom' ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:border-violet-300'}`}
                  >
                    <span className="flex items-center gap-2 text-sm font-bold text-slate-800"><Crosshair className="h-4 w-4" /> Choose or search on map</span>
                    <span className="mt-1 block text-[11px] text-slate-500">Search worldwide or drop and drag a precise pin.</span>
                  </button>
                </div>
              )}

              {coordinateMode && selectedPreviewPoint && (
                <ZoneGpsPicker
                  value={selectedPreviewPoint}
                  onChange={(point) => {
                    setCustomPoint(point);
                    setCoordinateMode('custom');
                  }}
                  biasCenter={toPoint(operationsArea?.center)}
                  biasRadiusKm={operationsArea?.radius_km}
                  countryIso2={operationsArea?.country_iso2}
                  readOnly={coordinateMode !== 'custom'}
                />
              )}

              {coordinateMode === 'custom' && !selectedPreviewPoint && (
                <ZoneGpsPicker
                  value={null}
                  onChange={setCustomPoint}
                  biasCenter={toPoint(operationsArea?.center)}
                  biasRadiusKm={operationsArea?.radius_km}
                  countryIso2={operationsArea?.country_iso2}
                />
              )}

              {selectedPreviewPoint && coordinateMode !== 'custom' && (
                <div className="flex items-start gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5 text-xs text-blue-800">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>Confirming will save <span className="font-mono font-bold">{selectedPreviewPoint.lat.toFixed(6)}, {selectedPreviewPoint.lng.toFixed(6)}</span> as the official incident point.</span>
                </div>
              )}

              {protectedCorrection && (
                <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="map-location-correction-warning">
                  <p className="text-xs font-bold text-amber-900">
                    Confirm incident location correction
                  </p>
                  {primaryLocationChanges && (
                    <p className="text-xs text-amber-800">
                      Location: {incident?.location_names?.[0] || 'Unknown Location'} → {displayName}.
                    </p>
                  )}
                  {gpsMappingChanges && (
                    <p className="text-xs text-amber-800" data-testid="map-gps-correction-summary">
                      Official GPS: {formatSource(projection.coordinate_source)} · {formatPoint(projection.official_coordinates || projection.coordinates)} → {formatSource(proposedCoordinateSource)} · {formatPoint(selectedPreviewPoint)}.
                    </p>
                  )}
                  <p className="text-xs text-amber-800">
                    The old and new values, your administrator identity, and the reason will be recorded in the audit log.
                  </p>
                  {correctionLockReason ? (
                    <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{correctionLockReason}</p>
                  ) : (
                    <textarea
                      value={correctionReason}
                      onChange={(event) => setCorrectionReason(event.target.value)}
                      maxLength={500}
                      rows={3}
                      data-testid="map-location-correction-reason"
                      placeholder="Explain why the originally reported location or GPS is incorrect."
                      className="w-full resize-none rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                    />
                  )}
                </div>
              )}

              {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{error}</p>}

              <div className="flex gap-3 border-t border-slate-200 pt-4">
                {!startsWithKnownLocation && (
                  <button type="button" onClick={() => { setStep(1); setCoordinateMode(''); }} className="flex items-center gap-1 rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50">
                    <ChevronLeft className="h-4 w-4" /> Back
                  </button>
                )}
                <button
                  type="button"
                  onClick={confirm}
                  disabled={saving || !coordinateMode || (coordinateMode === 'custom' && !customPoint) || (protectedCorrection && (!!correctionLockReason || !correctionReason.trim()))}
                  className="ml-auto flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {saving ? 'Confirming…' : 'Confirm Mapping'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
